// @intl project:microsite

import React, { FunctionComponent, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Display } from '../../atoms/display/Display'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { DisplaySize } from '../../styles/typeStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { Modal } from '../modal/Modal'

const ContentContainer = styled(Flex1View)<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['24px'],
}))

const CloseIconRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'flex-end',
})

const TitleContainer = styled(Display)<{ theme: ThemeType }>(({ theme }) => ({
  paddingTop: theme.spacing['16px'],
  paddingBottom: theme.spacing['16px'],
}))

const LegalCopyContainer = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['24px'],
}))

const ButtonContainer = styled.View<{ isMinWidthTablet: boolean }>(({ isMinWidthTablet }) => ({
  ...(isMinWidthTablet && {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  }),
}))

export type CoverageTermsContentProps = {
  legalLanguage: React.ReactNode
  fromLegalFooter?: boolean
} & (
  | { isOpen?: undefined; onClose?: undefined }
  | {
      /** Controlled open state. When provided, the component does not render its own trigger link. */ isOpen: boolean
      /** Called when the modal requests close. */ onClose: () => void
    }
)

export const CoverageTermsContent: FunctionComponent<CoverageTermsContentProps> = ({
  legalLanguage,
  fromLegalFooter = false,
  isOpen,
  onClose,
}) => {
  const { isMinWidthTablet, isMobileSized } = useMediaQuerySize()
  const [isInternalOpen, setIsInternalOpen] = useState(false)
  const isControlled = isOpen !== undefined
  const modalVisible = isControlled ? isOpen : isInternalOpen
  const closeModal = () => (onClose ? onClose() : setIsInternalOpen(false))
  const { colors, isAuroraTheme, isDarkMode } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  const modalContent = (
    <ContentContainer>
      <CloseIconRow>
        <PressableOpacity
          testID={tID('CoverageTermsContent-closeIcon')}
          onPress={closeModal}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close',
            description: 'Accessibility label for close button on coverage terms modal',
          })}
        >
          <XIcon size={24} fillColor={colors.iconDefault} />
        </PressableOpacity>
      </CloseIconRow>
      <TitleContainer
        testID={'CoverageTermsContent-title'}
        size={DisplaySize.XSMALL}
        color={colors.textPrimary}
        text={
          <FormattedMessage
            defaultMessage='Coverage'
            description='Title text for a modal dialog displaying legal language about service coverage'
          />
        }
      />
      <LegalCopyContainer
        testID={tID('CoverageTermsContent-modalContent')}
        color={colors.textPrimary}
        size={BodyTextSize.DEFAULT}
      >
        {React.isValidElement<{ color?: string }>(legalLanguage)
          ? React.cloneElement(legalLanguage, { color: colors.textPrimary })
          : legalLanguage}
      </LegalCopyContainer>
      <ButtonContainer isMinWidthTablet={isMinWidthTablet}>
        <PrimaryButton
          text={
            <FormattedMessage
              defaultMessage='Close'
              description='Button to close a modal dialog displaying legal language about service coverage'
            />
          }
          testID={tID('CoverageTermsContent-closeButton')}
          onPress={closeModal}
          fullWidth={isMobileSized}
        />
      </ButtonContainer>
    </ContentContainer>
  )
  return (
    <View testID={tID('CoverageTermsContent')}>
      <Modal
        disableBottomSheet={!isMobileSized}
        visible={modalVisible}
        modalContents={modalContent}
        onRequestClose={closeModal}
        onCloseEnd={closeModal}
        scrollable
        scrollableModalHeight={'100%'}
        scrollableStyle={{ padding: 0 }}
        showCloseIcon={false}
        customHeader={<View />}
        snapPoints={['73%']}
        backgroundColor={isAuroraTheme ? colors.backgroundModal : undefined}
        accessibilityLabel={formatMessage({
          defaultMessage: 'Coverage terms modal',
          description: 'Accessibility label for coverage terms modal',
        })}
      />
      {!isControlled && (
        <PressableOpacity
          testID={tID('CoverageTermsContent-linkButton')}
          onPress={() => setIsInternalOpen(true)}
          accessibilityRole='link'
          accessibilityLabel={formatMessage({
            defaultMessage: 'Coverage',
            description: 'Link to open the modal about customers coverage',
          })}
          importantForAccessibility='yes'
        >
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='Coverage'
                description='Link to open the modal about customers coverage'
              />
            }
            color={
              fromLegalFooter
                ? isAuroraTheme && isDarkMode
                  ? colors.linkDefault
                  : colors.textSecondary
                : colors.textActive
            }
            size={fromLegalFooter ? BodyTextSize.SMALL : BodyTextSize.CAPTION}
            underline={fromLegalFooter}
            medium={fromLegalFooter}
            // For Android we don't want to announce the text twice
            importantForAccessibility='no'
          />
        </PressableOpacity>
      )}
    </View>
  )
}
